import { toString } from 'lodash';
import { parseOptInValues } from './consentOptInValues';
import CMP_PRESETS from './AnalyticsBuilderConsentSource/CMP_PRESETS';
import parseConsentCodes from './parseConsentCodes';
import parseImpliedConsent from './parseImpliedConsent';
import CONSENT_SOURCE_TYPES, {
  DEFAULT_CONSENT_SOURCE_TYPE,
  DEFAULT_COOKIE_ENCODING,
  JS_API_PRIVACY_COOKIE_NAME,
  isNonCookieConsentSource,
} from './CONSENT_SOURCE_TYPES';
import {
  CONSENT_SOURCE_TYPE,
  CONSENT_SAVED_CALLBACK_SOURCE,
  CONSENT_CMP_PRESET,
  CONSENT_ONETRUST_COOKIE_NAME,
  CONSENT_COOKIE_ENCODING,
  CONSENT_COOKIE_WINDOW_VAR,
  CONSENT_JS_API_GLOBAL_OBJECT,
  CONSENT_JS_API_METHOD,
  CONSENT_JS_API_PROPERTY_PATH,
  CONSENT_OPT_IN_VALUES,
  CONSENT_GPC_ENABLED,
  CONSENT_GPC_CODES,
  CONSENT_MATCH_ID_CONSENT_CODE,
  CONSENT_MATCH_ID_ENABLED,
} from './form-keys';

/**
 * Builds read the DOM while YAML downloads read retained form state. Branch
 * only on the selected source so stale fields cannot change the consent source.
 */
export default function buildConsentSettings({ data, consentCategories }) {
  const consentBoolean = parseImpliedConsent(data.impliedConsent);
  // The category select unmounts when Match ID Consent is disabled, but the
  // formState-based download path can still carry a stale selection.
  const matchIDConsentCodeID =
    data[CONSENT_MATCH_ID_ENABLED] === 'false' ? '' : data[CONSENT_MATCH_ID_CONSENT_CODE] || '';
  const codes = consentCategories.map(category => category.code);
  const matchIDConsentCode = consentCategories.find(category => category.id === matchIDConsentCodeID)?.code;

  const sourceType = data[CONSENT_SOURCE_TYPE] ?? DEFAULT_CONSENT_SOURCE_TYPE;
  const consentSettings = {
    codes,
    explicitConsent: !consentBoolean,
    ...(matchIDConsentCode && { matchIDConsentCode }),
    matchIDConsentCodeID,
    consentSource: buildConsentSource({ data, sourceType }),
    globalPrivacyControl: buildGlobalPrivacyControl(data),
  };

  // Mirror the field's own default rather than leaving the key off and letting
  // the server derive it: the two build paths read different sources (the DOM
  // for a build, formState for a YAML download), so an omitted key made the
  // downloaded config disagree with the built file.
  consentSettings.optInValues =
    sourceType === CONSENT_SOURCE_TYPES.CMP && data[CONSENT_CMP_PRESET] === CMP_PRESETS.ONETRUST
      ? ['true']
      : parseOptInValues(data[CONSENT_OPT_IN_VALUES]);

  return consentSettings;
}

/**
 * Resolve the cookieName for the legacy `privacy` block. The builder-server
 * hard-validates `privacy` whenever it is present (getPrivacySettings throws
 * on a falsy cookieName), so jsApi builds send a placeholder — the consent
 * module's jsApi source is what actually drives consent decisions.
 *
 * @param {object} data - Form data
 * @returns {string} Cookie name for the privacy block
 */
export function resolvePrivacyCookieName(data) {
  if (isNonCookieConsentSource(data[CONSENT_SOURCE_TYPE])) {
    return JS_API_PRIVACY_COOKIE_NAME;
  }
  return data.cookieName;
}

function buildConsentSource({ data, sourceType }) {
  if (sourceType === CONSENT_SOURCE_TYPES.CALLBACK_JS_API) {
    let source;
    try {
      source = JSON.parse(data[CONSENT_SAVED_CALLBACK_SOURCE]);
    } catch {
      throw new Error(
        'The saved callback consent configuration is unavailable. Reopen the file or explicitly select another consent source.'
      );
    }
    if (source?.type !== CONSENT_SOURCE_TYPES.CALLBACK_JS_API) {
      throw new Error('The saved callback consent configuration does not match the selected source.');
    }
    return source;
  }
  if (sourceType === CONSENT_SOURCE_TYPES.CMP) {
    const provider = data[CONSENT_CMP_PRESET];
    const consentCookieName =
      provider === CMP_PRESETS.ONETRUST ? toString(data[CONSENT_ONETRUST_COOKIE_NAME]).trim() : '';
    return { type: CONSENT_SOURCE_TYPES.CMP, provider, ...(consentCookieName && { consentCookieName }) };
  }
  if (sourceType === CONSENT_SOURCE_TYPES.JS_API) {
    const propertyPath = data[CONSENT_JS_API_PROPERTY_PATH];
    return {
      type: CONSENT_SOURCE_TYPES.JS_API,
      globalObject: data[CONSENT_JS_API_GLOBAL_OBJECT],
      method: data[CONSENT_JS_API_METHOD],
      ...(propertyPath && { propertyPath }),
    };
  }

  if (sourceType !== CONSENT_SOURCE_TYPES.COOKIE) {
    throw new Error(`Unsupported consent source: ${sourceType}. Select a supported source before building.`);
  }
  const windowVarName = data[CONSENT_COOKIE_WINDOW_VAR];
  return {
    type: CONSENT_SOURCE_TYPES.COOKIE,
    cookieName: data.cookieName,
    encoding: data[CONSENT_COOKIE_ENCODING] ?? DEFAULT_COOKIE_ENCODING,
    ...(windowVarName && { windowVarName }),
  };
}

function buildGlobalPrivacyControl(data) {
  const enabled = data[CONSENT_GPC_ENABLED] === 'true';
  return {
    enabled,
    codes: enabled ? parseConsentCodes(data[CONSENT_GPC_CODES]) : [],
  };
}
